/**
 * Shared CAPTCHA controller — the one place every public form's spam-check lives, so the inline-
 * before-submit behavior applies uniformly (subscribe, report, ask, records, permit, resident
 * comment/survey) without forking per page.
 *
 * LIVE mode (behind CloudFront, VITE_WAF_CAPTCHA_* set): renders the AWS WAF puzzle INLINE into the
 * field's container ON MOUNT — the resident solves it as part of the form, then submits (the pattern
 * every site uses). Under WAF immunity the render resolves instantly with no visible puzzle and the
 * field shows a "verified" state. On solve, the token is stashed for header delivery (PR #206) and
 * `passed` flips true so the form's required-captcha gate is satisfied.
 *
 * FALLBACK mode (local dev / mocks / tests, CAPTCHA disabled): no live WAF, so there's nothing to
 * render on mount — `passed` is driven by the CaptchaField checkbox via `onChange`, exactly as before.
 *
 * SUBMIT SAFETY NET: WAF tokens are short-lived and only a `renderCaptcha` token counts, so on submit
 * the caller still awaits `ensureForSubmit()`. It's a fast no-op when the inline solve is still fresh,
 * and auto re-renders the puzzle (re-challenge) when the token is missing/expired — no dead end.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CAPTCHA_ENABLED,
  ensureCaptchaToken,
  renderCaptchaInline,
} from "../api/wafCaptcha.js";

/**
 * "idle" — fallback (checkbox) mode, or before the live render starts.
 * "loading" — live SDK loading / first render in flight (nothing to solve yet).
 * "challenging" — a visible puzzle is showing, awaiting the resident.
 * "verified" — solved (or WAF immunity) — the form's captcha gate is satisfied.
 * "error" — the render/solve failed; the field shows a retry.
 */
export type CaptchaStatus = "idle" | "loading" | "challenging" | "verified" | "error";

export interface UseCaptcha {
  /** Attach to CaptchaField's container ref — the SDK renders the puzzle here. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Live-mode status for CaptchaField + submit-button gating. */
  status: CaptchaStatus;
  /** Accessible error message when the render/solve fails. */
  error: string | null;
  /** True once the captcha is satisfied (solved, immune, or the fallback checkbox is ticked). */
  passed: boolean;
  /** Fallback-checkbox onChange (ignored in live mode). */
  onChange: (checked: boolean) => void;
  /**
   * Submit safety net — await before the gated POST. Resolves true when a fresh token is ready (fast
   * no-op if the inline solve is still fresh; re-challenges if missing/expired), false if the
   * resident couldn't complete a re-challenge (the caller then shows its error + aborts the submit).
   * Always true in fallback mode (the checkbox gate is enforced by the caller / `passed`).
   */
  ensureForSubmit: () => Promise<boolean>;
  /** Manually retry the inline render after a failure (wired to CaptchaField's retry). */
  retry: () => void;
}

const RENDER_FAIL_MSG = "We couldn't load the spam check. Please retry.";
const SUBMIT_FAIL_MSG = "We couldn't verify the spam check. Please try the puzzle again.";

export function useCaptcha(): UseCaptcha {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<CaptchaStatus>(CAPTCHA_ENABLED ? "loading" : "idle");
  const [error, setError] = useState<string | null>(null);
  // In fallback mode `passed` is the checkbox; in live mode it's set true once the puzzle solves.
  const [passed, setPassed] = useState(false);
  // Bump to re-run the mount effect on an explicit retry.
  const [renderNonce, setRenderNonce] = useState(0);
  // Re-entry guard: a second submit click while a re-challenge is in flight must not spin up a
  // second puzzle (Lister — "a second click can't spin up a second CAPTCHA"). A ref, not state, so
  // the guard is synchronous within one submit turn.
  const ensuringRef = useRef(false);

  // Inline render ON MOUNT (live only). Under immunity this resolves instantly → "verified".
  useEffect(() => {
    if (!CAPTCHA_ENABLED) return;
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    setError(null);
    setStatus("loading");
    // A visible puzzle appears asynchronously; flip to "challenging" once the SDK has had a beat to
    // paint (immunity resolves before this fires, so the flash of "challenging" is avoided in that
    // path because we only apply it while still unresolved).
    let resolved = false;
    const challengingTimer = window.setTimeout(() => {
      if (!cancelled && !resolved) setStatus("challenging");
    }, 400);

    renderCaptchaInline(el)
      .then(() => {
        resolved = true;
        if (cancelled) return;
        setPassed(true);
        setStatus("verified");
      })
      .catch(() => {
        resolved = true;
        if (cancelled) return;
        setPassed(false);
        setError(RENDER_FAIL_MSG);
        setStatus("error");
      })
      .finally(() => window.clearTimeout(challengingTimer));

    return () => {
      cancelled = true;
      window.clearTimeout(challengingTimer);
    };
  }, [renderNonce]);

  const onChange = useCallback((checked: boolean) => {
    // Fallback (checkbox) mode only — live mode drives `passed` from the puzzle solve.
    if (CAPTCHA_ENABLED) return;
    setPassed(checked);
  }, []);

  const retry = useCallback(() => {
    setRenderNonce((n) => n + 1);
  }, []);

  const ensureForSubmit = useCallback(async (): Promise<boolean> => {
    if (!CAPTCHA_ENABLED) return true; // fallback: the caller enforces the checkbox gate via `passed`.
    const el = containerRef.current;
    if (!el) return false;
    if (ensuringRef.current) return false; // a re-challenge is already in flight — ignore re-entry.
    ensuringRef.current = true;
    setError(null);
    try {
      // Fast no-op if the inline solve is still fresh; else re-renders the puzzle and awaits a solve.
      await ensureCaptchaToken(el);
      setPassed(true);
      setStatus("verified");
      return true;
    } catch {
      setPassed(false);
      setError(SUBMIT_FAIL_MSG);
      setStatus("error");
      el.focus?.();
      return false;
    } finally {
      ensuringRef.current = false;
    }
  }, []);

  return { containerRef, status, error, passed, onChange, ensureForSubmit, retry };
}
